import { FC, useMemo } from "react";
import { Button, Drawer, Empty, Skeleton, Spin, Tag } from "antd";
import { renderQuestionType } from "modules/organization/utility/commonFunctions";
import { useRankingSnapshotStore } from "../store/rankingSnapshotStore";

const compareOrderText = (a: string, b: string) => {
  const aParts = a.split(".").map(Number);
  const bParts = b.split(".").map(Number);
  const length = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < length; i += 1) {
    const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }
  return 0;
};

const SKELETON_QUESTION_COUNT = 3;
const SKELETON_ANSWER_COUNT = 2;

const SkeletonQuestions: FC = () => (
  <div className="qstTreeWrap medalDrawerQuestionsWrap">
    <ul>
      {Array.from({ length: SKELETON_QUESTION_COUNT }, (_, questionIndex) => (
        <li key={`skeleton-question-${questionIndex}`}>
          <div className="qstRowBlk samplingList">
            <span className="qstExpandTogglePlaceholder" />
            <div className="qstIconNumber">
              <Skeleton.Avatar active size="small" shape="circle" />
            </div>
            <div className="titleTxt tr">
              <Skeleton.Input active size="small" style={{ width: "70%" }} />
            </div>
            <span className="qstVariableTag tagWrap">
              <Skeleton.Button active size="small" style={{ width: 80 }} />
            </span>
          </div>
          <ul>
            {Array.from({ length: SKELETON_ANSWER_COUNT }, (_, answerIndex) => (
              <li
                key={`skeleton-answer-${questionIndex}-${answerIndex}`}
                className="qstInputList"
              >
                <span className="inputLabel">
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: "50%" }}
                  />
                </span>
                <span className="tagWrap">
                  <Skeleton.Button active size="small" style={{ width: 60 }} />
                </span>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  </div>
);

const MedalWinningQuestionsDrawer: FC = () => {
  const {
    isDrawerOpen,
    selectedRecord,
    medalQuestions,
    medalQuestionsLoading,
    medalQuestionsRefreshing,
    closeRecordDrawer,
  } = useRankingSnapshotStore();

  const sortedQuestions = useMemo(
    () =>
      [...medalQuestions].sort((a, b) =>
        compareOrderText(a.question_order_text, b.question_order_text),
      ),
    [medalQuestions],
  );

  const showEmptyState = !medalQuestionsLoading && sortedQuestions.length === 0;

  return (
    <Drawer
      open={isDrawerOpen}
      placement="right"
      width={560}
      title={
        <span className="medalDrawerTitleWrap">
          <h2 className="medalDrawerTitleLabel">
            {selectedRecord?.record_name}: Medal Winning Questions & Responses
          </h2>
        </span>
      }
      styles={{ header: { alignItems: "flex-start" } }}
      onClose={closeRecordDrawer}
      closable={false}
      maskClosable
      className="medalWinningQuestionsDrawer"
      extra={
        <Button
          onClick={closeRecordDrawer}
          className="closeBlk"
          aria-label="Close"
        />
      }
    >
      {medalQuestionsRefreshing && (
        <div className="medalDrawerRefreshingOverlay" aria-label="Refreshing">
          <Spin size="small" />
        </div>
      )}
      {medalQuestionsLoading ? (
        <SkeletonQuestions />
      ) : showEmptyState ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Question and response data is not available for this record."
        />
      ) : (
        <div className="qstTreeWrap medalDrawerQuestionsWrap">
          <ul>
            {sortedQuestions.map((question) => (
              <li key={question.question_id}>
                <div className="qstRowBlk samplingList">
                  <span className="qstExpandTogglePlaceholder" />
                  <div className="qstIconNumber">
                    {renderQuestionType(question.question_type_id)}
                    <span>Q{question.question_order_text}</span>
                  </div>
                  <div className="titleTxt tr">{question.description}</div>
                  <span className="qstVariableTag tagWrap">
                    <Tag>{question.variable}</Tag>
                  </span>
                </div>
                <ul>
                  {question.answes.length > 0 ? (
                    question.answes.map((answer) => (
                      <li key={answer.id} className="qstInputList">
                        <span className="inputLabel">{answer.description}</span>
                        {"  "}
                        <span className="tagWrap">
                          <Tag>{answer.variable}</Tag>
                        </span>
                      </li>
                    ))
                  ) : (
                    <li className="qstInputList">
                      <span className="inputLabel">No response recorded</span>
                    </li>
                  )}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
};

export default MedalWinningQuestionsDrawer;
